/**
 * ============================================================================
 * BUNDLE EXPRESS CHECKOUT FIX — Layer 5
 * ============================================================================
 *
 * Problem: the dynamic checkout buttons (Shop Pay / Apple Pay / Google Pay /
 * PayPal Express) rendered by `{{ form | payment_button }}` on the
 * Discovery Set bundle PDP bypass our bundle JS handler. Shopify's
 * express checkout grabs the items[N][id] inputs from the form but
 * doesn't reliably include the hidden items[N] entry for the parent
 * product line. Customers end up at checkout with only the $0 sample
 * children — no parent product — and pay shipping only. The merchant
 * team has to manually add the missing parent post-purchase and chase
 * an invoice. (Examples: orders DTC352128, DTC352155, DTC353384.)
 *
 * The smoking gun is DTC353384, which contains only 4 sample line
 * items — impossible via the standard "Add to Cart" button because
 * discovery-set-validator.js keeps the ATC button disabled until all
 * 5 dropdowns are picked. The customer must have used an express
 * checkout button that bypasses that gate AND the bundle JS handler.
 *
 * Fix: intercept express button clicks in the CAPTURE phase so we fire
 * before Shopify's own click handlers. Re-validate that all 5
 * fragrances are selected; rewrite the form's data-discount-url so the
 * existing bundle JS's success-redirect lands on /checkout (instead of
 * back on the product page); then dispatch a submit event so the
 * existing custom.js handler does its normal cart-add work (parent +
 * 5 children with the right line item properties). The customer lands
 * on /checkout with the bundle correctly populated and can pick Shop
 * Pay / Apple Pay / PayPal / Google Pay there.
 *
 * UX trade-off: one extra click vs. native express-on-PDP (PDP click →
 * /checkout → pick express method there), but the cart is guaranteed
 * to be priced correctly. The team stops needing to manually edit the
 * resulting orders.
 *
 * Note: this affects only the bundle PDP (the [data-product-bundle]
 * form). Express checkout on regular product pages, the cart page,
 * and the checkout page itself is untouched.
 * ============================================================================
 */

(function () {
  'use strict';

  const BUNDLE_FORM_SELECTOR = '[data-product-bundle]';
  const PAYMENT_BUTTON_SELECTOR =
    '.shopify-payment-button, [data-payment-button="true"], .productForm-buttons[data-payment-button]';
  const SELECT_INCOMPLETE_MESSAGE = 'Please select all 5 fragrances before checking out.';

  function getBundleFormIfPaymentClick(target) {
    if (!target || !target.closest) return null;
    const paymentContainer = target.closest(PAYMENT_BUTTON_SELECTOR);
    if (!paymentContainer) return null;
    return document.querySelector(BUNDLE_FORM_SELECTOR);
  }

  function allFragrancesSelected(bundleForm) {
    const selects = bundleForm.querySelectorAll('[data-bundle-select]');
    if (selects.length === 0) return true;
    for (let i = 0; i < selects.length; i++) {
      const v = selects[i].value;
      if (!v || !v.trim()) return false;
    }
    return true;
  }

  /**
   * Rewrite the bundle form's discount URL so the existing bundle JS,
   * after it successfully adds the bundle to cart, redirects the
   * customer to /checkout instead of back to the product page.
   *
   * jQuery caches data-* attributes after first read, so just updating
   * the DOM attribute isn't enough — we also poke jQuery's per-element
   * cache to make sure the bundle JS sees the new value when it calls
   * this.$form.data().discountUrl.
   */
  function rewireRedirectToCheckout(bundleForm) {
    const code = bundleForm.getAttribute('data-discount-code') || '';
    const newUrl = code
      ? '/discount/' + encodeURIComponent(code) + '?redirect=/checkout'
      : '/checkout';

    bundleForm.setAttribute('data-discount-url', newUrl);
    if (typeof window.jQuery === 'function') {
      try { window.jQuery(bundleForm).data('discountUrl', newUrl); } catch (e) { /* ignore */ }
    }
  }

  function interceptExpressClick(e) {
    const bundleForm = getBundleFormIfPaymentClick(e.target);
    if (!bundleForm) return; // not a payment button click on a bundle PDP

    // Block Shopify's express handler from running
    e.preventDefault();
    e.stopImmediatePropagation();

    if (!allFragrancesSelected(bundleForm)) {
      try { alert(SELECT_INCOMPLETE_MESSAGE); } catch (err) { /* ignore */ }
      return;
    }

    // Make the bundle JS's success-redirect land on /checkout
    rewireRedirectToCheckout(bundleForm);

    // Trigger the bundle form's submit so the existing custom.js handler
    // does the cart-add (parent + 5 children with the right properties).
    // On success it will redirect to the (now-rewritten) discount URL,
    // landing the customer on /checkout where they can pick Shop Pay,
    // Apple Pay, PayPal, Google Pay, etc.
    bundleForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  }

  // Capture phase so this fires BEFORE Shopify's payment button handlers
  document.addEventListener('click', interceptExpressClick, true);
})();
